import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import * as nav_const from '../../app-navigation';
import { environment } from 'src/environments/environment';
import { FeatureFlagsService } from './featureFlags.service';
import { UserService } from './user/user.service';
import {
  hasTerritoryRole,
  repTerritoryEnabled,
} from '../helpers/territory-access';
import { CatalogConfigurationsService } from './catalog-configurations.service';

export interface MenuItem {
  text?: string;
  path?: string;
  externalUrl?: string;
  icon?: string;
  items?: MenuItem[];
  expanded?: boolean;
  template?: string;
}

/**
 * DevExtreme keys tree items by `path`, so the same entry reaching the menu twice raises
 * E1040 and the menu fails to render. It can: a user who is both Seller and Administrator
 * collects `hydra_admin` from the seller pass and `admin` from the administrator pass, and
 * the two groups overlap (/admin/user-manager, among others). First occurrence wins, so the
 * earlier, more specific group keeps its position.
 */
export function dedupeMenuItems(
  items: MenuItem[],
  seen = new Set<string>(),
): MenuItem[] {
  const out: MenuItem[] = [];
  for (const item of items ?? []) {
    if (!item) continue;
    if (item.path) {
      if (seen.has(item.path)) continue;
      seen.add(item.path);
    }
    out.push(
      item.items?.length
        ? { ...item, items: dedupeMenuItems(item.items, seen) }
        : item,
    );
  }
  return out;
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private menuItemsSubject = new BehaviorSubject<MenuItem[]>([]);
  menuItems$ = this.menuItemsSubject.asObservable();

  private userInfoSubject = new BehaviorSubject<{
    user: any;
    medigiAdmin: boolean;
  } | null>(null);
  userInfo$ = this.userInfoSubject.asObservable();

  user: any;
  client: any;
  medigiAdmin = false;
  rfpEnabled = true;
  bidEnabled = true;

  cartTemplateEnabled = this.featureFlagsService.get('cart_template');
  cartTemplateScheduleEnabled = this.featureFlagsService.get(
    'cart_template_schedule',
  );
  coaSearchEnabled = this.featureFlagsService.get('coa_menu_item');
  territoriesEnabled = !!this.featureFlagsService.getByEnv(
    'process',
    'territories',
  );

  /** The flag plus a Territory role; Administrator alone does not show it. */
  get territoriesVisible(): boolean {
    return this.territoriesEnabled && hasTerritoryRole(this.user?.roles);
  }

  constructor(
    private userService: UserService,
    private featureFlagsService: FeatureFlagsService,
    private catalogConfigsService: CatalogConfigurationsService,
  ) {}

  updateMenu(menuOpened: boolean = false) {
    this.userService.getMe().subscribe(user => {
      this.user = user.data;
      this.medigiAdmin = this.user.email.includes('admin@medigi');
      this.userInfoSubject.next({
        user: this.user,
        medigiAdmin: this.medigiAdmin,
      });

      let items: MenuItem[] = [];

      if (!environment.hydraEnabled) {
        items = this.getMedigiItems(menuOpened);
      } else {
        this.getHydraItems(menuOpened).then(hydraItems => {
          this.menuItemsSubject.next(dedupeMenuItems(hydraItems));
        });
        return;
      }

      this.menuItemsSubject.next(dedupeMenuItems(items));
    });
  }

  private getMedigiItems(menuOpened: boolean): MenuItem[] {
    let items: MenuItem[] = [];

    if (this.user.is_representative) {
      return this.repItems();
    }

    if (this.user.roles.includes('Buyer')) {
      items = this.addBuyerItems(items, menuOpened);
    }

    if (this.user.roles.includes('Seller')) {
      items = this.addSellerItems(items, menuOpened);
    }

    if (this.user.roles.includes('Administrator')) {
      items = items.concat(nav_const.admin);
      // a Seller already has it in the seller menu
      if (this.territoriesVisible && !this.user.roles.includes('Seller')) {
        items = items.concat(nav_const.territories);
      }
      if (this.coaSearchEnabled) {
        items = items.concat(nav_const.COASearch);
      }
    }

    if (this.user.roles.includes('Buyer')) {
      items = items.concat(nav_const.reports);
    }

    items = items.concat(nav_const.lowerMenu);
    return items;
  }

  private async getHydraItems(menuOpened: boolean): Promise<MenuItem[]> {
    this.client = await this.userService._getMsClient();
    this.rfpEnabled = this.client?.hydra_config?.rfp_enabled;
    this.bidEnabled = this.client?.hydra_config?.bid_enabled;

    let items: MenuItem[] = [];

    if (this.user.roles.includes('Buyer')) {
      items = this.getHydraBuyerItems(items, menuOpened);
    }

    if (this.user.roles.includes('Seller')) {
      items = this.getHydraSellerItems(items, menuOpened);
    }

    if (this.user.roles.includes('Administrator')) {
      items = items.concat(nav_const.admin);
      // a Seller already has it in the seller menu
      if (this.territoriesVisible && !this.user.roles.includes('Seller')) {
        items = items.concat(nav_const.territories);
      }
      if (this.coaSearchEnabled) {
        items = items.concat(nav_const.COASearch);
      }
    }

    items = items.concat(nav_const.lowerMenu);
    return items;
  }

  /** Sales and customer reps; My Territory with a Territory role. */
  private repItems(): MenuItem[] {
    const items = [...nav_const.sales_representative];
    return repTerritoryEnabled(this.featureFlagsService) &&
      hasTerritoryRole(this.user?.roles)
      ? items.concat(nav_const.my_territory)
      : items;
  }

  private addBuyerItems(items: MenuItem[], menuOpened: boolean): MenuItem[] {
    let buyerItems = [...items];
    if (location.host === environment.clientDomains.medshortsDomain) {
      buyerItems = buyerItems.concat(nav_const.medigi);
    }

    buyerItems = buyerItems.concat(nav_const.buyer);
    buyerItems = this.setBuyerSideCatalogMenu(
      buyerItems,
      'Catalogs',
      menuOpened,
    );
    buyerItems = buyerItems.concat(nav_const.bids);

    if (window.location.host !== environment.clientDomains.medshortsDomain) {
      buyerItems = buyerItems.concat(nav_const.rfps);
    }
    buyerItems = buyerItems.concat(nav_const.buyer_requests);

    if (this.cartTemplateEnabled) {
      buyerItems = buyerItems.concat(nav_const.cart_template);
    }
    if (this.cartTemplateScheduleEnabled) {
      buyerItems = buyerItems.concat(nav_const.cart_schedule);
    }
    if (this.user.roles.includes('CSOS Signer')) {
      buyerItems = buyerItems.concat(nav_const.csos);
    }

    if (
      this.user.roles.includes('CompanyAdmin') ||
      this.user.roles.includes('FacilityAdmin')
    ) {
      buyerItems = buyerItems.concat(nav_const.buyer_editing);
    }
    return buyerItems;
  }

  private addSellerItems(items: MenuItem[], menuOpened: boolean): MenuItem[] {
    let sellerItems = [...items].concat(nav_const.seller_home);

    if (this.user.company.is_503b) {
      sellerItems = sellerItems.concat(nav_const.seller_listings_503b);
    } else {
      sellerItems = sellerItems.concat(nav_const.seller_listings);
    }

    sellerItems = sellerItems.concat(nav_const.seller_default);
    sellerItems = this.setSellerSideListingMenu(sellerItems, menuOpened);

    if (!this.user.is_field_rep) {
      sellerItems = sellerItems.concat(nav_const.reports);
    }

    if (this.user.roles.includes('CompanyAdmin')) {
      sellerItems = sellerItems.concat(nav_const.seller_admin);
    }
    // role + flag, same as the hydra seller menu: hydraEnabled does not decide it
    if (this.territoriesVisible) {
      sellerItems = sellerItems.concat(nav_const.territories);
    }
    sellerItems = sellerItems.concat(nav_const.seller_guides);
    return sellerItems;
  }

  private getHydraBuyerItems(
    items: MenuItem[],
    menuOpened: boolean,
  ): MenuItem[] {
    let buyerItems = [...items];
    if (this.user.is_representative) {
      return this.repItems();
    }

    buyerItems = buyerItems.concat(nav_const.hydra_buyer);
    buyerItems = this.setBuyerSideCatalogMenu(
      buyerItems,
      'Catalog',
      menuOpened,
    );

    if (this.bidEnabled) {
      buyerItems = buyerItems.concat(nav_const.bids);
    }
    if (this.rfpEnabled) {
      buyerItems = buyerItems.concat(nav_const.rfps);
    }
    if (this.cartTemplateEnabled) {
      buyerItems = buyerItems.concat(nav_const.cart_template);
    }
    if (this.cartTemplateScheduleEnabled) {
      buyerItems = buyerItems.concat(nav_const.cart_schedule);
    }
    if (this.user.roles.includes('CSOS Signer')) {
      buyerItems = buyerItems.concat(nav_const.csos);
    }
    if (
      this.user.roles.includes('CompanyAdmin') ||
      this.user.roles.includes('FacilityAdmin')
    ) {
      buyerItems = buyerItems.concat(nav_const.buyer_editing);
    }
    buyerItems = buyerItems.concat(nav_const.reports_only);

    if (this.coaSearchEnabled) {
      buyerItems = buyerItems.concat(nav_const.COASearch);
    }
    return buyerItems;
  }

  private getHydraSellerItems(
    items: MenuItem[],
    menuOpened: boolean,
  ): MenuItem[] {
    let sellerItems = [...items].concat(nav_const.seller_hydra_listings);
    sellerItems = sellerItems.concat(nav_const.seller_hydra_default);

    if (this.bidEnabled) {
      sellerItems = sellerItems.concat(nav_const.bids);
    }
    if (this.rfpEnabled) {
      sellerItems = sellerItems.concat(nav_const.rfps);
    }

    sellerItems = sellerItems.concat(nav_const.quicksight_only);

    if (this.user.is_admin) {
      sellerItems = sellerItems.concat(nav_const.hydra_admin);
    }
    // Territory Managers edit their company's territories, Territory Viewers read them
    if (this.territoriesVisible) {
      sellerItems = sellerItems.concat(nav_const.territories);
    }
    if (this.user.is_admin) {
      if (this.cartTemplateEnabled) {
        sellerItems = sellerItems.concat(nav_const.cart_template);
      }
      if (this.cartTemplateScheduleEnabled) {
        sellerItems = sellerItems.concat(nav_const.cart_schedule);
      }
    }
    sellerItems = sellerItems.concat(nav_const.reports_only);
    if (this.coaSearchEnabled) {
      sellerItems = sellerItems.concat(nav_const.COASearch);
    }

    // White-label company admins manage company-wide rules here too (MDG-112);
    // News Editor stays medigi-only, so this adds the Company entry alone.
    if (this.user.roles.includes('CompanyAdmin')) {
      sellerItems = sellerItems.concat(nav_const.seller_company);
    }

    // Flag-gated Companies tab for Pricing-role users (MDG-158).
    if (
      this.featureFlagsService.get('pricing_company_editor') &&
      !this.user.is_admin &&
      this.user.roles.includes('Pricing')
    ) {
      sellerItems = sellerItems.concat(nav_const.pricing_companies);
    }

    sellerItems = this.setSellerSideListingMenu(sellerItems, menuOpened);

    return sellerItems;
  }

  private setBuyerSideCatalogMenu(
    items: MenuItem[],
    term: string,
    menuOpened: boolean,
  ): MenuItem[] {
    const buyerSideCatalogMenu = this.featureFlagsService.get(
      'buyer_side_catalog_menu',
    );
    if (
      buyerSideCatalogMenu &&
      buyerSideCatalogMenu[environment.supplierConfig.clientNameSpace]
    ) {
      const insertIndex = items.findIndex(item => item.text === term);
      if (insertIndex !== -1) {
        const allItemsMenu = {
          text: 'All Items',
          path: '/catalog',
          icon: 'catalogs',
        };
        items[insertIndex] = {
          text: term,
          icon: 'catalogs',
          items: [
            allItemsMenu,
            ...buyerSideCatalogMenu[environment.supplierConfig.clientNameSpace],
          ],
          expanded: !!menuOpened,
        };
      }
    }
    return items;
  }

  private setSellerSideListingMenu(
    items: MenuItem[],
    menuOpened: boolean,
  ): MenuItem[] {
    const sellerSideListingMenu = this.featureFlagsService.get(
      'seller_side_listing_menu',
    );
    if (
      sellerSideListingMenu &&
      sellerSideListingMenu[environment.supplierConfig.clientNameSpace]
    ) {
      const insertIndex = items.findIndex(item => item.text === 'Listings');
      if (insertIndex !== -1) {
        items[insertIndex] = {
          text: 'Listings',
          icon: 'listings',
          items: [
            ...sellerSideListingMenu[
              environment.supplierConfig.clientNameSpace
            ],
          ],
          expanded: !!menuOpened,
        };
      }
    }
    return items;
  }
}
